import React from 'react';
import { useTranslation } from 'react-i18next';
import { EntityTypes } from '@guryou/core';
import { printDateTime, printPrice } from '@guryou/utils';
import Icon from '../shared/Icon';

const ReservationDetails = ({ event, order }) => {
  const { t } = useTranslation();

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('GENERAL.noDataAvailable')}</p>
      </div>
    );
  }

  const providerName = event.providerName || event.provider_name || event.provider?.name;
  const getStatusColor = status => {
    switch (status) {
      case 'A':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'C':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'P':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = status => {
    switch (status) {
      case 'A':
        return t('GENERAL.active');
      case 'C':
        return t('GENERAL.cancel');
      case 'P':
        return t('GENERAL.pending');
      default:
        return status;
    }
  };

  const InfoItem = ({ icon, label, value, iconColor = 'text-blue-600' }) => {
    if (!value && value !== 0) {
      return null;
    }

    return (
      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
        <div className={`flex-shrink-0 ${iconColor} mt-0.5`}>
          <Icon name={icon} size="md" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-sm text-gray-900 mt-1 font-semibold break-words">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providerName && <InfoItem icon="building" label={t('GENERAL.provider', 'Centro')} value={providerName} iconColor="text-purple-600" />}

        {event.startTime && <InfoItem icon="calendar" label={t('GENERAL.from')} value={printDateTime(event.startTime)} iconColor="text-blue-600" />}

        {event.endTime && <InfoItem icon="calendar" label={t('GENERAL.to')} value={printDateTime(event.endTime)} iconColor="text-blue-600" />}

        {event.duration && (
          <InfoItem
            icon="clock"
            label={t('PROFILE.duration')}
            value={`${event.duration} ${event.timeUnit ? `${event.timeUnit}s` : t('GENERAL.minutes')}`}
            iconColor="text-indigo-600"
          />
        )}

        {(() => {
          let resourceValue = null;
          if (event.resources && Array.isArray(event.resources) && event.resources.length > 0) {
            resourceValue = event.resources.map(r => (typeof r === 'string' ? r : r.name)).join(', ');
          } else if (event.resourceName) {
            resourceValue = event.resourceName;
          }

          return resourceValue ? <InfoItem icon="user" label={t('PROFILE.resource')} value={resourceValue} iconColor="text-green-600" /> : null;
        })()}

        {order?.price !== undefined && order.price !== null && (
          <InfoItem icon="currency-dollar" label={t('GENERAL.price')} value={printPrice(order.price)} iconColor="text-emerald-600" />
        )}

        {event.status && (
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0 text-orange-600 mt-0.5">
              <Icon name="info" size="md" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('GENERAL.status')}</p>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(event.status)}`}>
                {getStatusText(event.status)}
              </span>
            </div>
          </div>
        )}

        {event.entityType !== EntityTypes.service && event.capacity !== undefined && (
          <InfoItem
            icon="users"
            label={t('GENERAL.capacity')}
            value={`${event.capacity || 0} / ${event.totalCapacity || 0}`}
            iconColor="text-pink-600"
          />
        )}

        {event.waitingListPosition && event.waitingListPosition > 0 && (
          <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex-shrink-0 text-yellow-600 mt-0.5">
              <Icon name="info" size="md" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-yellow-800 uppercase tracking-wide">{t('GENERAL.waitingList', 'Lista de Espera')}</p>
              <p className="text-sm text-yellow-900 mt-1 font-semibold">
                {t('GENERAL.position')}: {event.waitingListPosition}
              </p>
            </div>
          </div>
        )}
      </div>

      {order?.sessions && order.sessions.length > 1 && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Icon name="calendar" size="md" color="#6B7280" />
            <span>{t('SESSIONS.titlePlural')}</span>
          </h4>
          <div className="space-y-3">
            {order.sessions.map((session, index) => (
              <div
                key={session.id || index}
                className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 hover:shadow-md transition-shadow"
              >
                <div className="space-y-2">
                  {session.startTime && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center space-x-2">
                        <Icon name="calendar" size="sm" color="#6B7280" />
                        <span>{t('GENERAL.from')}:</span>
                      </span>
                      <span className="text-gray-900 font-semibold">{printDateTime(session.startTime)}</span>
                    </div>
                  )}
                  {session.endTime && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center space-x-2">
                        <Icon name="calendar" size="sm" color="#6B7280" />
                        <span>{t('GENERAL.to')}:</span>
                      </span>
                      <span className="text-gray-900 font-semibold">{printDateTime(session.endTime)}</span>
                    </div>
                  )}
                  {session.resourceName && (
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-blue-200">
                      <span className="text-gray-600 flex items-center space-x-2">
                        <Icon name="user" size="sm" color="#6B7280" />
                        <span>{t('PROFILE.resource')}:</span>
                      </span>
                      <span className="text-gray-900 font-medium">{session.resourceName}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationDetails;
